import "./RichTextEditor.scss";
import { MDXEditor, MDXEditorMethods, MDXEditorProps, ViewMode } from "@mdxeditor/editor";
import {
    toolbarPlugin,
    codeBlockPlugin,
    codeMirrorPlugin,
    diffSourcePlugin,
    directivesPlugin,
    headingsPlugin,
    imagePlugin,
    linkDialogPlugin,
    linkPlugin,
    listsPlugin,
    markdownShortcutPlugin,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
} from "@mdxeditor/editor";
import { materialDark } from "cm6-theme-material-dark";
import { keymap } from "@codemirror/view";
import { indentWithTab } from "@codemirror/commands";
import { QerentRichTextEditorToolbar } from "./Toolbar";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { useCallback, useEffect, useRef, useState } from "react";
import { updateParameterValue } from "../../../store/storyline/actions";
import _ from "lodash";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { MentionDirectiveDescriptor } from "./mentions/directive";
import type { MentionOption } from "./mentions/command-menu";
import { mentionShortcutPlugin } from "./mentions/editor-plugin";
import { getDataProps } from "../../../shared/utilities";

export type RichTextEditorProps = {
    name?: string;
    initialValue?: string;
    className?: string;
    onChange?: (value: string) => boolean | void;
    imageUploadHandler?: (file: File) => Promise<string>;
    availableViews: ViewMode[];
    mentionOptions?: MentionOption[];
};

const AVAILABLE_LANGUAGES = {
    jsx: "JavaScript (React)",
    css: "CSS",
    python: "Python",
    rust: "Rust",
    js: "JavaScript",
    javascript: "JavaScript",
    ts: "TypeScript",
    bash: "Bash",
    powershell: "PowerShell",
    json: "JSON",
    markdown: "Markdown",
    mermaid: "Mermaid Diagram",
    toml: "TOML",
    yaml: "YAML",
    txt: "Plain Text",
    qerent: "Qerent Template (Rendered)",
    documentation: "Documentation Sample"
};

const RichTextEditor: React.FC<RichTextEditorProps> = (props) => {
    const { name, initialValue, className, onChange: _onChange, imageUploadHandler, availableViews, mentionOptions } = props;
    const dispatch = useThunkDispatch();
    const ref = useRef<MDXEditorMethods>(null);
    const parameterValue = useSelector((s: RootState) => s.storyline.parameterValues?.get?.(name) as string);
    const [originalValue] = useState(parameterValue ?? initialValue ?? "");
    const [value, setValue] = useState(originalValue);

    const onChange = useCallback(_.debounce((newValue: string) => {
        if (_onChange) {
            const shouldContinue = _onChange(newValue);
            if (shouldContinue === false) return;
        }

        setValue(newValue);
        if (name) {
            dispatch(updateParameterValue(name, newValue));
        }
    }, 500), [_onChange]);

    useEffect(() => {
        if (parameterValue !== undefined && !_.isEqual(parameterValue, value)) {
            setValue(parameterValue);
            ref?.current?.setMarkdown(parameterValue);
        }
    }, [parameterValue]);

    return (
        <div className={clsx("RichTextEditor", className)} {...getDataProps(props)}>
            <MDXEditor
                ref={ref}
                data-name={name}
                markdown={value}
                onChange={onChange}
                contentEditableClassName="markdown"
                plugins={[
                    toolbarPlugin({ toolbarContents: () => <QerentRichTextEditorToolbar availableViews={availableViews ?? ["rich-text", "source"]} /> }),
                    listsPlugin(),
                    quotePlugin(),
                    headingsPlugin({ allowedHeadingLevels: [1, 2, 3] }),
                    linkPlugin(),
                    linkDialogPlugin(),
                    imagePlugin({ imageUploadHandler }),
                    tablePlugin(),
                    thematicBreakPlugin(),
                    codeBlockPlugin({ defaultCodeBlockLanguage: "jsx" }),
                    codeMirrorPlugin({
                        codeBlockLanguages: AVAILABLE_LANGUAGES,
                        autoLoadLanguageSupport: true,
                        codeMirrorExtensions: [materialDark, keymap.of([indentWithTab])]
                    }),
                    directivesPlugin({ directiveDescriptors: [MentionDirectiveDescriptor] }),
                    diffSourcePlugin({ viewMode: "rich-text", diffMarkdown: originalValue, codeMirrorExtensions: [materialDark] }),
                    mentionShortcutPlugin({ options: mentionOptions ?? [] }),
                    markdownShortcutPlugin(),
                ]}
            />
        </div>
    );
};

(RichTextEditor as DocumentedComponent).metadata = {
    description: "The `RichTextEditor` component provides a WYSIWYG (What You See Is What You Get) interface for editing formatted content.  This content is stored in Markdown format and can be rendered effortlessly via the [`Markdown`](/canvases/markdown) component.  The raw Markdown content can also be edited directly via the `source` tab.\n\nBy supplying a parameter path (via the `name` prop), the content can be read from and persisted to the parameter automatically (bi-directional binding).",
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "The name of the variable that the value(s) will be read from and persisted to. The underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value." },
        { name: "initialValue", type: "string", description: "The initial value of the text editor.  Only applies when the parameter with the specified name is empty (or if no parameter is specified). Optional - no default." },
        { name: "className", type: "string", description: "Additional CSS classes to apply to the text editor.  Optional - no default." },
        { name: "onChange", type: "function", exactType: "(value: string) => boolean", template: "onChange={(value) => {$1}}", description: "The (optional) callback function to invoke when the text editor value has changed.  The function is passed the new value as its only argument.  If the function returns `false`, the value will not be persisted to the parameter.  Optional - no default." },
        { name: "imageUploadHandler", type: "function", exactType: "(file: File) => Promise<string>", template: "imageUploadHandler={(file) => {$1}}", description: "The (optional) callback function to invoke when an image is uploaded.  The function is passed the file as its only argument.  The function should return a Promise that resolves to the URL of the uploaded image.  If not provided, only existing URLs can be used for image elements (no upload function available in the pop-up).  Optional - no default." },
        { name: "availableViews", type: "object", exactType: "Array<'rich-text' | 'diff' | 'source'>", template: `availableViews={["rich-text", "diff", "source"]}`, description: "The (optional) list of available views for the text editor.  The available options are `\"rich-text\"`, `\"diff\"` and `\"source\"`.  Optional - defaults to `[\"rich-text\", \"source\"]`." },
        { name: "mentionOptions", type: "object", exactType: "{ value: any, label: string, picture?: string }[]", description: "The (optional) list of available mention options.  If provided, the provided values will be presented to the user when the `@` symbol is used within the editor.  Values selected by the user will be added to the Markdown as a [directive](https://talk.commonmark.org/t/generic-directives-plugins-syntax/444) named `mention`, with the `label` field used as the content and the `value` field included in the attributes.  Optional - no default." },
    ]
};

export { RichTextEditor };